import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SERVICES, waMessage } from "../lib/brand";

function ServiceCard({ svc, index }) {
  const isHighlight = svc.highlight;
  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.8, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className={`svc-card ${isHighlight ? "lg:col-span-6" : "lg:col-span-6"}`}
      data-testid={`service-card-${svc.id}`}
      id={svc.anchor}
    >
      <div className="svc-image">
        <img src={svc.image} alt={svc.title} loading="lazy" />
        {svc.badge && (
          <div className="absolute top-4 left-4">
            <span className="badge-gold" data-testid={`badge-${svc.id}`}>
              ★ {svc.badge}
            </span>
          </div>
        )}
      </div>

      <div className="flex-1 flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <span className="font-display text-2xl italic text-[#0F766E] leading-none">
            {svc.number}
          </span>
          <span className="text-[0.66rem] uppercase tracking-[0.24em] font-semibold text-[#4a4a4a]">
            Service · {svc.number}
          </span>
        </div>

        <h3 className="font-display text-[1.9rem] lg:text-[2.4rem] leading-[1.05] tracking-tight text-[#222] mt-1">
          {svc.title}
        </h3>

        <p className="mt-4 font-body text-[#4a4a4a] text-[0.98rem] leading-relaxed font-light max-w-md">
          {svc.description}
        </p>

        {svc.pills?.length > 0 && (
          <div className="mt-5 flex flex-wrap gap-2" data-testid={`pills-${svc.id}`}>
            {svc.pills.map((p) => (
              <span key={p} className="pill">
                {p}
              </span>
            ))}
          </div>
        )}

        <div className="mt-8 flex items-center justify-between border-t hairline pt-5">
          <a
            href={waMessage(svc.id)}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-[0.85rem] font-semibold text-[#0F766E] group"
            data-testid={`service-cta-${svc.id}`}
          >
            {svc.cta}
            <ArrowUpRight
              size={16}
              strokeWidth={1.7}
              className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
            />
          </a>
          <span className="text-[0.66rem] uppercase tracking-[0.28em] font-semibold text-[rgba(34,34,34,0.35)]">
            WhatsApp Instant
          </span>
        </div>
      </div>
    </motion.article>
  );
}

export default function Services() {
  return (
    <section
      id="services"
      className="relative py-24 lg:py-32"
      data-testid="services-section"
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 lg:mb-20 items-end">
          <div className="lg:col-span-7">
            <div className="overline mb-6" data-testid="services-overline">
              Top Priority Services
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-[4.4rem] leading-[1.02] tracking-tighter text-[#222]">
              Four ways we serve you —{" "}
              <span className="italic text-[#0F766E]">from everyday rides</span> to
              long-term requirements.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="font-body text-[#4a4a4a] text-lg leading-relaxed font-light">
              Choose the service that fits your requirement. Share your preferred car, date, duration or event details and PAAT will discuss availability and the final quote with you.
            </p>
          </div>
        </div>

        {/* Featured highlight card full-width */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Card 1 spanning full width */}
          <motion.article
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="svc-card lg:col-span-12 lg:!p-8 lg:grid lg:grid-cols-12 lg:gap-8"
            data-testid={`service-card-${SERVICES[0].id}`}
            id={SERVICES[0].anchor}
          >
            <div className="svc-image lg:col-span-6 lg:!mb-0 lg:!aspect-auto lg:h-[440px]">
              <img src={SERVICES[0].image} alt={SERVICES[0].title} />
              <div className="absolute top-4 left-4">
                <span className="badge-gold">★ {SERVICES[0].badge}</span>
              </div>
            </div>
            <div className="lg:col-span-6 flex flex-col justify-center">
              <div className="flex items-center justify-between mb-3">
                <span className="font-display text-3xl italic text-[#0F766E] leading-none">
                  {SERVICES[0].number}
                </span>
                <span className="text-[0.66rem] uppercase tracking-[0.24em] font-semibold text-[#4a4a4a]">
                  Featured Service
                </span>
              </div>
              <h3 className="font-display text-[2.2rem] sm:text-[2.8rem] lg:text-[3.6rem] leading-[1.02] tracking-tight text-[#222]">
                {SERVICES[0].title}
              </h3>
              <p className="mt-5 font-body text-[#4a4a4a] text-lg leading-relaxed font-light max-w-lg">
                {SERVICES[0].description}
              </p>
              <ul className="mt-6 space-y-2 text-[0.92rem] font-body text-[#4a4a4a]">
                {[
                  "Instructor and car can be arranged at your doorstep",
                  "Choose car-only or car + instructor service",
                  "Licence assistance can be arranged if required",
                ].map((f, i) => (
                  <li key={i} className="flex gap-3 items-start">
                    <span className="mt-2 w-2 h-2 rounded-full bg-[#0F766E]" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href={waMessage("driving_school")}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-emerald"
                  data-testid="featured-book-instructor-btn"
                >
                  Book Driving Service
                  <ArrowUpRight size={16} strokeWidth={1.6} />
                </a>
                <a
                  href="#contact"
                  className="btn-outline"
                  data-testid="featured-request-quote-btn"
                >
                  Request Quote
                </a>
              </div>
            </div>
          </motion.article>

          {/* Remaining 4 cards */}
          {SERVICES.slice(1).map((svc, i) => (
            <ServiceCard key={svc.id} svc={svc} index={i + 1} />
          ))}
        </div>
      </div>
    </section>
  );
}
